import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * One close control, everywhere.
 *
 * A cream disc with a warm hairline border and a light ink cross, sized as a
 * comfortable touch target. Replaces the assortment of bare `X` glyphs (dialog,
 * sheet, lightbox, dismissible banners) that each disagreed on size, contrast
 * and shape, and read as low-contrast on cream.
 *
 * `surface` adapts the contrast to what the button sits on:
 *   - 'default' — cream / white / card surfaces
 *   - 'dark'    — the ink hero/header surfaces
 *   - 'amber'   — the brand-amber fill
 *
 * Exposed both as a class factory (for Radix primitives whose Close element we
 * cannot replace, only style) and as a ready button (for ad-hoc closes).
 */
export function closeButtonClass(surface: 'default' | 'dark' | 'amber' = 'default') {
  return cn(
    'inline-grid h-9 w-9 place-items-center rounded-full border transition-colors',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    surface === 'dark' && 'border-white/25 bg-white/15 text-white hover:bg-white/25',
    surface === 'amber' && 'border-kanfy-on-amber/20 bg-background text-kanfy-on-amber hover:brightness-95',
    surface === 'default' && 'border-border bg-background text-foreground shadow-e1 hover:bg-muted',
  );
}

export interface CloseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  surface?: 'default' | 'dark' | 'amber';
  /** Accessible name; defaults to "Close". */
  label?: string;
}

export const CloseButton = React.forwardRef<HTMLButtonElement, CloseButtonProps>(
  function CloseButton({ surface = 'default', label = 'Close', className, ...props }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        className={cn(closeButtonClass(surface), className)}
        {...props}
      >
        <X className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
      </button>
    );
  },
);
